import { DeliveryModel } from '@audibene/ta.utils-opportunity'
import {
  ConsentDocumentType as LegacyConsentDocumentType,
  DocumentType as LegacyDocumentType,
} from 'constants/documentType'
import { useConsentDocuments as useLegacyConsents } from '../useConsentDocuments'
import { useCustomer } from '../useCustomer'
import { useDocuments as useLegacyDocuments } from '../useDocuments'
import { DocumentType } from 'types'
import { useCustomerDocumentSignings } from 'hooks/useCustomerDocumentSignings'
import { useMemo } from 'react'
import { useCoreConsents } from 'hooks/useCoreConsents'
import { CmmsConsentType } from 'constants/cmmsConsent'

const isCustomerUS = (countryCode?: string) => countryCode === 'US'
const allLegacyConsentDocumentTypes = Object.values(LegacyConsentDocumentType)

export const EMPTY_DOCUMENT_SIGNING_STATUSES = {
  isDataPrivacyAgreementSigned: false,
  isMedicalWaiverSigned: false,
  isPurchaseDeliveryReceiptSigned: false,
  isTeleaudiologyConsented: false,
} as const

const useCustomerDocumentSigningStatuses = () => {
  const { data: customer, isError: haveCustomerErrored, isLoading: isCustomerLoading } = useCustomer()
  const customerSalesforceId = customer?.salesforceId
  const isTaLight = customer?.journeyDetails?.deliveryModel === DeliveryModel.TA_LIGHT

  const {
    data: legacyConsents = [],
    isError: haveLegacyConsentsErrored,
    isLoading: areLegacyConsentsLoading,
  } = useLegacyConsents(
    {
      // We're certain that customerSalesforceId is not null because the query is disabled if it is not provided
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      customerSalesforceId: customerSalesforceId!,
      type: allLegacyConsentDocumentTypes,
    },
    { enabled: Boolean(customerSalesforceId) && !isTaLight }
  )

  const {
    data: legacyDocuments = [],
    isError: haveLegacyDocumentsErrored,
    isLoading: areLegacyDocumentsLoading,
  } = useLegacyDocuments(
    {
      // We're certain that customerSalesforceId is not null because the query is disabled if it is not provided
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      customerSalesforceId: [customerSalesforceId!],
    },
    { enabled: Boolean(customerSalesforceId) && !isTaLight }
  )

  const {
    data: customerSignings = [],
    isError: haveCustomerDocumentSigningsErrored,
    isLoading: areCustomerDocumentSigningsLoading,
  } = useCustomerDocumentSignings({
    enabled: isCustomerUS(customer?.countryCode) && !isTaLight,
  })

  const {
    data: cmmsConsents = [],
    isError: haveCoreConsentsErrored,
    isLoading: areCoreConsentsLoading,
  } = useCoreConsents(
    {
      customerGid: customer?.gid as string,
    },
    { enabled: Boolean(customer?.gid) }
  )

  const isError =
    haveCustomerErrored ||
    haveLegacyConsentsErrored ||
    haveLegacyDocumentsErrored ||
    haveCustomerDocumentSigningsErrored ||
    haveCoreConsentsErrored

  const isLoading =
    isCustomerLoading ||
    areLegacyConsentsLoading ||
    areLegacyDocumentsLoading ||
    areCustomerDocumentSigningsLoading ||
    areCoreConsentsLoading

  const isLegacyHIPAANoticeConsented = legacyConsents.some(
    ({ type }) => type === LegacyConsentDocumentType.HIPAA_NOTICE
  )

  const isLegacyMedicalWaiverConsented = legacyConsents.some(
    ({ type }) => type === LegacyConsentDocumentType.MEDICAL_WAIVER
  )

  const isLegacyHIPAANoticeSigned = legacyDocuments.some(
    ({ is_signed, type }) => type === LegacyDocumentType.HIPAA_NOTICE && is_signed
  )

  const isLegacyMedicalWaiverSigned = legacyDocuments.some(
    ({ is_signed, type }) => type === LegacyDocumentType.MEDICAL_WAIVER && is_signed
  )

  const isLegacyPurchaseDeliveryReceiptSigned = legacyDocuments.some(
    ({ is_signed, type }) => type === LegacyDocumentType.PURCHASE_DELIVERY_RECEIPT && is_signed
  )

  const isDataPrivacyAgreementSigned = customerSignings.some(
    ({ isCompleted, requiredDocument: { documentType } }) =>
      documentType === DocumentType.DATA_PRIVACY_AGREEMENT && isCompleted
  )

  const isMedicalWaiverSigned = customerSignings.some(
    ({ isCompleted, requiredDocument: { documentType } }) => documentType === DocumentType.MEDICAL_WAIVER && isCompleted
  )

  const isPurchaseDeliveryReceiptSigned = customerSignings.some(
    ({ isCompleted, requiredDocument: { documentType } }) =>
      documentType === DocumentType.PURCHASE_DELIVERY_RECEIPT && isCompleted
  )

  const isHipaaNoticeConsented = cmmsConsents.some(
    ({ granted, type }) => granted && type === CmmsConsentType.HIPAA_NOTICE
  )

  const isMedicalWaiverConsented = cmmsConsents.some(
    ({ granted, type }) => granted && type === CmmsConsentType.MEDICAL_WAIVER
  )

  const isTeleaudiologyConsented = cmmsConsents.some(
    ({ granted, type }) => granted && type === CmmsConsentType.TELEAUDIOLOGY_CONSENT
  )

  const areAnyDataPrivacyAgreementsSigned =
    isDataPrivacyAgreementSigned || isLegacyHIPAANoticeConsented || isLegacyHIPAANoticeSigned || isHipaaNoticeConsented

  const areAnyMedicalWaiversSigned =
    isMedicalWaiverSigned || isLegacyMedicalWaiverConsented || isLegacyMedicalWaiverSigned || isMedicalWaiverConsented

  const areAnyPurchaseDeliveryReceiptsSigned = isPurchaseDeliveryReceiptSigned || isLegacyPurchaseDeliveryReceiptSigned

  const isValid = !isError && !isLoading

  const data = useMemo(() => {
    if (!isValid) return undefined
    if (isTaLight) return { ...EMPTY_DOCUMENT_SIGNING_STATUSES }
    return {
      isDataPrivacyAgreementSigned: areAnyDataPrivacyAgreementsSigned,
      isMedicalWaiverSigned: areAnyMedicalWaiversSigned,
      isPurchaseDeliveryReceiptSigned: areAnyPurchaseDeliveryReceiptsSigned,
      isTeleaudiologyConsented,
    }
  }, [
    isValid,
    isTaLight,
    isTeleaudiologyConsented,
    areAnyDataPrivacyAgreementsSigned,
    areAnyMedicalWaiversSigned,
    areAnyPurchaseDeliveryReceiptsSigned,
  ])

  return useMemo(() => ({ data, isError, isLoading }), [data, isError, isLoading])
}

export { useCustomerDocumentSigningStatuses }
